/**
 * License-availability math, shared across the People license pickers.
 *
 * A license term carries a single signed quantityAvailable: positive is
 * seats still open, negative means it has been over-activated (more people
 * hold it than the term allows). Every consumer needs the same handful of
 * derived numbers, so they live here once instead of being re-derived inline.
 *
 * Note the single-term identity: one term can never be both available and
 * over-activated, so for availabilityOf exactly one of available /
 * overActivated is non-zero, capacity equals Math.abs(quantityAvailable),
 * and percentAvailable is either 0 or 100 (hence isLow is always false for a
 * single term). Only the aggregate across several terms can land in between,
 * which is why isLow is meaningful only for aggregateAvailability.
 */
export interface Availability {
  /** Seats still open (never negative). */
  available: number;
  /** Seats over the limit (never negative). */
  overActivated: number;
  /** available + overActivated. */
  capacity: number;
  /** available / capacity as a percentage (0 when capacity is 0). */
  percentAvailable: number;
  /** No seats open and at least one over the limit. */
  isOverActivated: boolean;
  /** Some seats open but under 20% of capacity (aggregate-only in practice). */
  isLow: boolean;
}

const buildAvailability = (available: number, overActivated: number): Availability => {
  const capacity = available + overActivated;
  const percentAvailable = capacity > 0 ? (available / capacity) * 100 : 0;
  return {
    available,
    overActivated,
    capacity,
    percentAvailable,
    isOverActivated: available === 0 && overActivated > 0,
    isLow: available > 0 && percentAvailable < 20,
  };
};

/** Availability of a single license term from its signed quantity. */
export const availabilityOf = (quantityAvailable: number): Availability =>
  buildAvailability(Math.max(0, quantityAvailable), Math.abs(Math.min(0, quantityAvailable)));

/** Availability summed across several license terms. */
export const aggregateAvailability = (quantitiesAvailable: ReadonlyArray<number>): Availability =>
  buildAvailability(
    quantitiesAvailable.reduce((sum, q) => sum + Math.max(0, q), 0),
    quantitiesAvailable.reduce((sum, q) => sum + Math.abs(Math.min(0, q)), 0),
  );
